// screens/documents/hooks/useDocument.ts
import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { getSalesApi } from '../../../../salesApi/salesApi'
import { selectCurrentCluster } from '../../../../store/slices/currentClusterSlice'
import { DocumentInterface } from '../../../../common/types/interfaces'

const initialValue: DocumentInterface = {
  name: '',
  description: '',
  isPrivate: false,
  links: [],
}

export const useDocument = () => {
  const [modalActive, setModalActive] = useState<boolean>(false)
  const [edit, setEdit] = useState<boolean>(false)
  const [salesApi] = useState(getSalesApi())
  const [data, setData] = useState<DocumentInterface>(initialValue)
  const [list, setList] = useState<DocumentInterface[]>([])
  const [detailsModalActive, setDetailsModalActive] = useState<boolean>(false)
  const [current, setCurrent] = useState<DocumentInterface>()
  const [currentFileUrl, setCurrentFileUrl] = useState<string>('')
  const currentCluster = useSelector(selectCurrentCluster)
  const [loading, setLoading] = useState(true)

  const getList = useCallback(async () => {
    try {
      const result = await salesApi.getDocumentFileList()
      if (result) setList(result.data)
      setLoading(false)
    } catch (error) {
      setLoading(false)
    }
  }, [salesApi])

  const getPublicFileUrl = useCallback(
    (el: DocumentInterface) => {
      if (!el.filePath || !currentCluster) return ''
      return `/sales-api/${currentCluster.port}/${currentCluster.token}/api/v1/data/file?path=${el.filePath}`
    },
    [currentCluster],
  )

  const getPreviewUrl = useCallback(
    (el: DocumentInterface) => {
      if (!el.mimeType?.startsWith('image/')) return undefined
      if (el.isPrivate) return undefined
      return getPublicFileUrl(el)
    },
    [getPublicFileUrl],
  )

  const download = useCallback(
  async (el: DocumentInterface) => {
    if (!el.id) return
    try {
      const blob = await salesApi.downloadDocumentFile(el.id)
      const url = URL.createObjectURL(blob)

      const ext = el.filePath?.split('.').pop() || ''
      const fileName = ext && !el.name.endsWith(`.${ext}`) ? `${el.name}.${ext}` : el.name

      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.log(error)
    }
  },
  [salesApi],
)

  const startCreate = () => {
    setCurrent(undefined)
    setEdit(false)
    setData({ ...initialValue })
    setModalActive(true)
  }

  const startEdit = (el: DocumentInterface) => {
    setCurrent(el)
    setEdit(true)
    setData({ ...el, links: el.links ?? [] })
    setModalActive(true)
  }

  const showDetails = useCallback(
    async (el: DocumentInterface) => {
      setCurrent(el)
      setDetailsModalActive(true)

      if (!el.id) return

      if (!el.isPrivate) {
        setCurrentFileUrl(getPublicFileUrl(el))
      } else {
        try {
          const blob = await salesApi.downloadDocumentFile(el.id)
          setCurrentFileUrl(URL.createObjectURL(blob))
        } catch {
          setCurrentFileUrl('')
        }
      }
    },
    [salesApi, getPublicFileUrl],
  )

  useEffect(() => {
    if (currentCluster) getList()
  }, [currentCluster, getList])

  useEffect(() => {
    if (!modalActive) setData(initialValue)
  }, [modalActive])

  useEffect(() => {
    if (!detailsModalActive && currentFileUrl.startsWith('blob:')) {
      URL.revokeObjectURL(currentFileUrl)
      setCurrentFileUrl('')
    }
  }, [detailsModalActive, currentFileUrl])

  return {
    getList,
    modalActive,
    setModalActive,
    startCreate,
    startEdit,
    edit,
    data,
    setData,
    list,
    detailsModalActive,
    setDetailsModalActive,
    showDetails,
    current,
    loading,
    download,
    getPreviewUrl,
    getPublicFileUrl,
    currentFileUrl,
  }
}
